import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCalculator } from '@react-icons/all-files/fa/FaCalculator';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useAppDispatch, useAppSelector } from '../store';
import { getResumen } from '../manager/workflow-instancia-estado-nodos.reducer';
import { fetchInboxLeidoCounts } from '../manager/profase-usuario-inbox.reducer';
import { APLICACION_ACCEDER_REQUIRED_AUTHORITIES, canAccessDashboard, canAccessEmpresas } from '../shared/authorization';
import PermissionDeniedScreen from '../shared/PermissionDeniedScreen';
import { playSound } from '../shared/audio/audio';
import {
  buildEmpresasVencimientoFilterUrl,
  cantidadEmpresaVencimientoPorIndicador,
  EMPRESA_VENCIMIENTO_COLORS,
  EMPRESA_VENCIMIENTO_INDICADORES,
  type EmpresaVencimientoFilterParam,
  type EmpresaVencimientoResumen,
  fetchEmpresaVencimientoFielResumen,
  fetchEmpresaVencimientoSellosResumen,
} from '../shared/empresa/empresa-vencimiento-resumen';
import CalcularVencimientoModal from './Home/calcular-vencimiento-modal';
import {
  cantidadEstadoNodosVencidasAgregadas,
  cantidadEstadoNodosPorEstado,
  findEstadoNodosResumenMetaPorEstado,
} from '../shared/workflow/estado-nodos-resumen.utils';
import {
  cantidadUsuarioActividadVencidasAgregadas,
  cantidadUsuarioActividadPorEstado,
  isUsuarioActividadResumenVencidoAgregado,
} from '../shared/usuario/usuario-actividad-resumen.utils';

const SELECT_BUTTON_SOUND = '/assets/audio/select-button.mp3';

function playDashboardClickSound(): void {
  playSound(SELECT_BUTTON_SOUND);
}

interface UsuarioActividadEstadoResumen {
  estado?: string | null;
  vencido?: boolean | null;
  descripcion?: string | null;
  cantidad: number;
  porcentaje: number;
}

interface MisTareasPieEntry {
  id: string;
  estado: string;
  vencido: boolean;
  name: string;
  value: number;
  color: string;
}

interface InstanciaEstadoResumen {
  estado: string;
  cantidad: number;
  porcentaje?: number;
}

const MIS_TAREAS_ESTADOS = ['BLOQUEADO', 'PENDIENTE', 'EN_PROCESO', 'ENVIADO', 'FINALIZADO'] as const;

const MIS_TAREAS_LABELS: Record<(typeof MIS_TAREAS_ESTADOS)[number], string> = {
  BLOQUEADO: 'Tareas Bloqueadas',
  PENDIENTE: 'Pendientes',
  EN_PROCESO: 'En Proceso',
  ENVIADO: 'Pendientes de Cerrar',
  FINALIZADO: 'Finalizadas',
};

const MIS_TAREAS_COLORS: Record<(typeof MIS_TAREAS_ESTADOS)[number], string> = {
  BLOQUEADO: '#94a3b8',
  PENDIENTE: '#f59e0b',
  EN_PROCESO: '#3b82f6',
  ENVIADO: '#f97316',
  FINALIZADO: '#22c55e',
};

const MIS_TAREAS_PIE_GRAY = '#94a3b8';
const MIS_TAREAS_PIE_RED = '#dc2626';

function blendHexColor(from: string, to: string, ratio: number): string {
  const parse = (hex: string) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  };
  const [r1, g1, b1] = parse(from);
  const [r2, g2, b2] = parse(to);
  const mix = (a: number, b: number) => Math.round(a + (b - a) * ratio);
  const channels = [mix(r1, r2), mix(g1, g2), mix(b1, b2)];
  return `#${channels.map(c => c.toString(16).padStart(2, '0')).join('')}`;
}

/** Pie: Bloqueado + vencido — mezcla gris/rojo. */
const MIS_TAREAS_BLOQUEADO_VENCIDO_PIE_COLOR = blendHexColor(MIS_TAREAS_PIE_GRAY, MIS_TAREAS_PIE_RED, 0.55);

const MIS_TAREAS_VENCIDO_COLOR = '#dc2626';

function cantidadTareasPorEstado(
  rows: UsuarioActividadEstadoResumen[],
  estado: string,
): number {
  return cantidadUsuarioActividadPorEstado(rows, estado);
}

function cantidadTareasVencidas(rows: UsuarioActividadEstadoResumen[]): number {
  return cantidadUsuarioActividadVencidasAgregadas(rows);
}

const MIS_TAREAS_PIE_LABELS: Record<(typeof MIS_TAREAS_ESTADOS)[number], string> = {
  BLOQUEADO: 'Bloqueado',
  PENDIENTE: 'Pendiente',
  EN_PROCESO: 'En Proceso',
  ENVIADO: 'Pendiente de Cerrar',
  FINALIZADO: 'Finalizado',
};

function misTareasPieColor(estado: string, vencido?: boolean): string {
  const up = estado.toUpperCase();
  if (up === 'BLOQUEADO') {
    return vencido === true
      ? MIS_TAREAS_BLOQUEADO_VENCIDO_PIE_COLOR
      : MIS_TAREAS_PIE_GRAY;
  }
  if (vencido === true) return MIS_TAREAS_VENCIDO_COLOR;
  const key = up as (typeof MIS_TAREAS_ESTADOS)[number];
  return MIS_TAREAS_COLORS[key] ?? '#64748b';
}

function normalizeMisTareasPieData(rows: UsuarioActividadEstadoResumen[]): MisTareasPieEntry[] {
  return (rows ?? [])
    .filter(row => !isUsuarioActividadResumenVencidoAgregado(row))
    .map(row => {
      const estado = String(row.estado ?? '').toUpperCase();
      const vencido = row.vencido === true;
      const value = Number(row.cantidad) || 0;
      const descripcion = String(row.descripcion ?? '').trim();
      const mapped = MIS_TAREAS_PIE_LABELS[estado as (typeof MIS_TAREAS_ESTADOS)[number]];
      const fallback = mapped ?? estado;

      return {
        id: `${estado}-${vencido ? 'vencido' : 'vigente'}`,
        estado,
        vencido,
        name: mapped ?? (descripcion || fallback),
        value,
        color: misTareasPieColor(estado, vencido),
      };
    })
    .filter(d => d.value > 0);
}

const inboxData = [
  { dia: 'Lun', mensajes: 3 },
  { dia: 'Mar', mensajes: 7 },
  { dia: 'Mié', mensajes: 2 },
  { dia: 'Jue', mensajes: 5 },
  { dia: 'Vie', mensajes: 4 },
  { dia: 'Sáb', mensajes: 1 },
  { dia: 'Dom', mensajes: 0 },
];

/** Map API `color` hint to bar fill (hex). */
const RESUMEN_COLOR_MAP: Record<string, string> = {
  green: '#22c55e',
  gray: '#94a3b8',
  grey: '#94a3b8',
  yellow: '#eab308',
  red: '#ef4444',
  blue: '#3b82f6',
  skyblue: '#38bdf8',
  black: '#1e293b',
  purple: '#8b5cf6',
  orange: '#f97316',
};

const ACTIVIDADES_CHART_ESTADOS = [
  { id: 'BLOQUEADO', label: 'Bloqueado', color: 'gray' },
  { id: 'PENDIENTE', label: 'Pendiente', color: 'purple' },
  { id: 'EN_PROCESO', label: 'En Proceso', color: 'blue' },
  { id: 'FINALIZADO', label: 'Finalizado', color: 'green' },
] as const;

const ACTIVIDADES_CHART_CLICKABLE = new Set<string>(
  ACTIVIDADES_CHART_ESTADOS.map(e => e.id),
);

const PROCESO_ESTADO_LABELS: Record<string, string> = {
  NUEVO: 'Nuevo',
  GENERADO: 'Generado',
  EN_PROCESO: 'En Proceso',
  PENDIENTE_DE_CORREGIR: 'Pend. de Corregir',
  FINALIZADO: 'Finalizado',
  ANULADO: 'Anulado',
  EN_PROCESO_Y_ARCHIVADO: 'Archivado',
  TERMINADO_Y_ARCHIVADO: 'Archivado',
  FINALIZADO_Y_ARCHIVADO: 'Archivado',
};

const PROCESO_ESTADO_COLORS: Record<string, string> = {
  NUEVO: '#64748b',
  GENERADO: '#3b82f6',
  EN_PROCESO: '#2563eb',
  PENDIENTE_DE_CORREGIR: '#f59e0b',
  FINALIZADO: '#22c55e',
  ANULADO: '#ef4444',
  EN_PROCESO_Y_ARCHIVADO: '#94a3b8',
  TERMINADO_Y_ARCHIVADO: '#94a3b8',
  FINALIZADO_Y_ARCHIVADO: '#94a3b8',
};

function resumenColorToHex(color?: string): string {
  if (!color) return '#64748b';
  if (color.startsWith('#')) return color;
  return RESUMEN_COLOR_MAP[color.toLowerCase()] ?? color;
}

function normalizeEmpresaVencimientoPieData(rows: EmpresaVencimientoResumen[]) {
  const byIndicador = new Map(
    (rows ?? []).map(r => [String(r.indicador).trim(), r]),
  );

  return EMPRESA_VENCIMIENTO_INDICADORES.map(({ id, label, color }) => {
    const existing = byIndicador.get(id);
    return {
      indicador: id,
      name: label,
      value: Number(existing?.cantidad) || 0,
      color: resumenColorToHex(existing?.color ?? color),
    };
  }).filter(d => d.value > 0);
}

function normalizeActividadesChartData(rows: any[]) {
  return ACTIVIDADES_CHART_ESTADOS.map(({ id, label, color }) => {
    const existing = findEstadoNodosResumenMetaPorEstado(rows, id);
    return {
      estado: id,
      label,
      cantidad: cantidadEstadoNodosPorEstado(rows, id),
      fill: resumenColorToHex(existing?.color ?? color),
    };
  });
}

function procesoEstadoLabel(estado: string): string {
  return PROCESO_ESTADO_LABELS[String(estado).toUpperCase()] ?? estado;
}

function procesoEstadoColor(estado: string): string {
  return PROCESO_ESTADO_COLORS[String(estado).toUpperCase()] ?? '#64748b';
}

function normalizeProcesosPieData(rows: InstanciaEstadoResumen[]) {
  return (rows ?? [])
    .map(row => {
      const estado = String(row.estado).toUpperCase();
      return {
        estado,
        name: procesoEstadoLabel(estado),
        value: Number(row.cantidad) || 0,
        color: procesoEstadoColor(estado),
      };
    })
    .filter(d => d.value > 0);
}

function isActividadesBarClickable(estado: string): boolean {
  return ACTIVIDADES_CHART_CLICKABLE.has(String(estado).toUpperCase());
}

/** Unread count from `leido-counts` rows (`leido === false` → `cantidad`). */
function unreadCantidadFromLeidoCounts(rows: any[]): number {
  if (!Array.isArray(rows)) return 0;
  const row = rows.find((r: any) => r.leido === false);
  return Number(row?.cantidad) || 0;
}

// ── Sub-components ─────────────────────────────────────────────────────────

interface CardProps {
  title: string;
  subtitle: string;
  linkLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}

function DashboardCard({ title, subtitle, linkLabel, onClick, children }: CardProps) {
  const handleLinkClick = () => {
    playDashboardClickSound();
    onClick();
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <p style={styles.cardTitle}>{title}</p>
          <p style={styles.cardSubtitle}>{subtitle}</p>
        </div>
        <button style={styles.linkBtn} onClick={handleLinkClick}>
          {linkLabel} →
        </button>
      </div>
      <div style={styles.chartArea}>{children}</div>
    </div>
  );
}

function DashboardSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

// Custom pie label showing percentage
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  if (percent < 0.06) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const account = useAppSelector(s => s.authentication.account);
  const canDashboard = canAccessDashboard(account?.authorities);
  const canEmpresas = canAccessEmpresas(account?.authorities);

  const resumen = useAppSelector((s: any) => s.workflowInstanciaEstadoNodos.resumen ?? []);
  const loadingResumen = useAppSelector((s: any) => s.workflowInstanciaEstadoNodos.loadingResumen);
  const leidoCounts = useAppSelector((s: any) => s.profaseUsuarioInbox?.leidoCounts ?? []);
  const loadingLeidoCounts = useAppSelector((s: any) => s.profaseUsuarioInbox?.loadingLeidoCounts ?? false);
  const [tareasResumen, setTareasResumen] = useState<UsuarioActividadEstadoResumen[]>([]);
  const [loadingTareasResumen, setLoadingTareasResumen] = useState(false);
  const [sellosResumen, setSellosResumen] = useState<EmpresaVencimientoResumen[]>([]);
  const [loadingSellosResumen, setLoadingSellosResumen] = useState(false);
  const [fielResumen, setFielResumen] = useState<EmpresaVencimientoResumen[]>([]);
  const [loadingFielResumen, setLoadingFielResumen] = useState(false);
  const [procesosEstadosResumen, setProcesosEstadosResumen] = useState<InstanciaEstadoResumen[]>([]);
  const [loadingProcesosEstadosResumen, setLoadingProcesosEstadosResumen] = useState(false);
  const [calcularVencimientoOpen, setCalcularVencimientoOpen] = useState(false);

  const fetchTareasResumen = useCallback(async () => {
    setLoadingTareasResumen(true);
    try {
      const res = await axios.get<UsuarioActividadEstadoResumen[]>(
        `api/usuario/me/actividad/resumen?cacheBuster=${Date.now()}`,
      );
      setTareasResumen(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTareasResumen([]);
    } finally {
      setLoadingTareasResumen(false);
    }
  }, []);

  const fetchSellosResumen = useCallback(async () => {
    setLoadingSellosResumen(true);
    try {
      setSellosResumen(await fetchEmpresaVencimientoSellosResumen());
    } finally {
      setLoadingSellosResumen(false);
    }
  }, []);

  const fetchFielResumen = useCallback(async () => {
    setLoadingFielResumen(true);
    try {
      setFielResumen(await fetchEmpresaVencimientoFielResumen());
    } finally {
      setLoadingFielResumen(false);
    }
  }, []);

  const fetchProcesosEstadosResumen = useCallback(async () => {
    setLoadingProcesosEstadosResumen(true);
    try {
      const res = await axios.get<InstanciaEstadoResumen[]>(
        `api/profase-workflow-instancias/estados/resumen?cacheBuster=${Date.now()}`,
      );
      setProcesosEstadosResumen(Array.isArray(res.data) ? res.data : []);
    } catch {
      setProcesosEstadosResumen([]);
    } finally {
      setLoadingProcesosEstadosResumen(false);
    }
  }, []);

  useEffect(() => {
    if (!canDashboard) return;
    dispatch(getResumen());
    dispatch(fetchInboxLeidoCounts());
    fetchTareasResumen();
    fetchProcesosEstadosResumen();
    if (canEmpresas) {
      fetchSellosResumen();
      fetchFielResumen();
    }
  }, [
    dispatch,
    canDashboard,
    canEmpresas,
    fetchTareasResumen,
    fetchProcesosEstadosResumen,
    fetchSellosResumen,
    fetchFielResumen,
  ]);

  const mensajesSinLeer = useMemo(() => unreadCantidadFromLeidoCounts(leidoCounts), [leidoCounts]);

  const actividadesData = useMemo(
    () => normalizeActividadesChartData(resumen),
    [resumen],
  );
  const actividadesVencidas = useMemo(
    () => cantidadEstadoNodosVencidasAgregadas(resumen),
    [resumen],
  );

  const misTareasPieData = useMemo(
    () => normalizeMisTareasPieData(tareasResumen),
    [tareasResumen],
  );

  const misTareasTotal = useMemo(
    () => tareasResumen.reduce((s, r) => s + (Number(r.cantidad) || 0), 0),
    [tareasResumen],
  );

  const misTareasVencidas = useMemo(
    () => cantidadTareasVencidas(tareasResumen),
    [tareasResumen],
  );

  const sellosPieData = useMemo(
    () => normalizeEmpresaVencimientoPieData(sellosResumen),
    [sellosResumen],
  );

  const sellosTotal = useMemo(
    () => sellosResumen.reduce((s, r) => s + (Number(r.cantidad) || 0), 0),
    [sellosResumen],
  );

  const fielPieData = useMemo(
    () => normalizeEmpresaVencimientoPieData(fielResumen),
    [fielResumen],
  );

  const fielTotal = useMemo(
    () => fielResumen.reduce((s, r) => s + (Number(r.cantidad) || 0), 0),
    [fielResumen],
  );

  const procesosPieData = useMemo(
    () => normalizeProcesosPieData(procesosEstadosResumen),
    [procesosEstadosResumen],
  );

  const procesosTotal = useMemo(
    () => procesosEstadosResumen.reduce((s, r) => s + (Number(r.cantidad) || 0), 0),
    [procesosEstadosResumen],
  );

  const navigateEmpresasVencimiento = useCallback((
    param: EmpresaVencimientoFilterParam,
    indicador: string,
  ) => {
    const url = buildEmpresasVencimientoFilterUrl(param, indicador);
    if (!url) return;
    playDashboardClickSound();
    navigate(url);
  }, [navigate]);

  const onSellosPieClick = (data: any) => {
    const indicador = String(data?.payload?.indicador ?? data?.indicador ?? '').trim();
    const value = Number(data?.payload?.value ?? data?.value ?? 0);
    if (indicador && value > 0) {
      navigateEmpresasVencimiento('vencimientoSellosEstado', indicador);
    }
  };

  const onFielPieClick = (data: any) => {
    const indicador = String(data?.payload?.indicador ?? data?.indicador ?? '').trim();
    const value = Number(data?.payload?.value ?? data?.value ?? 0);
    if (indicador && value > 0) {
      navigateEmpresasVencimiento('vencimientoFielEstado', indicador);
    }
  };

  const onMisTareasPieClick = (data: any) => {
    const payload = data?.payload ?? data;
    const estado = String(payload?.estado ?? '').toUpperCase();
    const vencido = payload?.vencido === true;
    const value = Number(payload?.value ?? 0);
    if (estado && value > 0) {
      playDashboardClickSound();
      const params = new URLSearchParams();
      if (estado === 'ENVIADO') {
        params.set('mailStatus', 'ENVIADO');
      } else {
        params.set('estado', estado);
      }
      if (vencido) params.set('vencido', 'true');
      navigate(`/mis-tareas?${params.toString()}`);
    }
  };

  const navigateActividadesEstado = useCallback((estado: string) => {
    const estadoUp = String(estado).toUpperCase();
    if (!isActividadesBarClickable(estadoUp)) return;

    playDashboardClickSound();
    const p = new URLSearchParams();
    p.set('page', '1');
    p.set('misActividades', 'N');
    if (estadoUp === 'FINALIZADO') {
      p.set('sort', 'workflowEstado,asc');
      p.set('nodoEstado', 'FINALIZADO');
      navigate(`/actividades?${p.toString()}`);
      return;
    }
    if (estadoUp === 'PENDIENTE') {
      p.set('nodoEstado', 'PENDIENTE');
      navigate(`/actividades?${p.toString()}`);
      return;
    }
    if (estadoUp === 'BLOQUEADO') {
      p.set('nodoEstado', 'BLOQUEADO');
      navigate(`/actividades?${p.toString()}`);
      return;
    }
    if (estadoUp === 'EN_PROCESO') {
      p.set('nodoEstado', 'EN_PROCESO');
      navigate(`/actividades?${p.toString()}`);
    }
  }, [navigate]);

  const onActividadesBarClick = (data: any) => {
    const payload = data?.payload ?? data;
    navigateActividadesEstado(String(payload?.estado ?? ''));
  };

  const onProcesosPieClick = (data: any) => {
    const estado = String(data?.payload?.estado ?? data?.estado ?? '').toUpperCase();
    const value = Number(data?.payload?.value ?? data?.value ?? 0);
    if (estado && value > 0) {
      playDashboardClickSound();
      navigate(`/procesos?estado=${encodeURIComponent(estado)}&page=1`);
    }
  };

  if (!canDashboard) {
    return (
      <PermissionDeniedScreen
        requiredAuthorities={APLICACION_ACCEDER_REQUIRED_AUTHORITIES}
        title="Dashboard no disponible"
        backTo="/about"
        backLabel="Ir a Acerca"
      />
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.pageTitle}>Dashboard</h2>
          <p style={styles.pageSubtitle}>Resumen general del CRM</p>
        </div>
        <button
          type="button"
          style={styles.actionBtn}
          onClick={() => {
            playDashboardClickSound();
            setCalcularVencimientoOpen(true);
          }}
        >
          <FaCalculator size={14} aria-hidden="true" />
          Calcular Vencimiento
        </button>
      </div>

      <CalcularVencimientoModal
        open={calcularVencimientoOpen}
        onClose={() => setCalcularVencimientoOpen(false)}
      />

      <DashboardSection title="Tareas">
        <div style={styles.statStrip}>
          {MIS_TAREAS_ESTADOS.map(estado => {
            const value = cantidadTareasPorEstado(tareasResumen, estado);
            return (
              <StatBadge
                key={estado}
                label={MIS_TAREAS_LABELS[estado]}
                value={value}
                color={MIS_TAREAS_COLORS[estado]}
                loading={loadingTareasResumen}
                onClick={
                  !loadingTareasResumen && value > 0
                    ? () => {
                        if (estado === 'ENVIADO') {
                          navigate('/mis-tareas?mailStatus=ENVIADO');
                          return;
                        }
                        navigate(`/mis-tareas?estado=${estado}`);
                      }
                    : undefined
                }
              />
            );
          })}
          <StatBadge
            label="Vencidas"
            value={misTareasVencidas}
            color={MIS_TAREAS_VENCIDO_COLOR}
            loading={loadingTareasResumen}
            onClick={
              !loadingTareasResumen && misTareasVencidas > 0
                ? () => {
                    playDashboardClickSound();
                    navigate('/mis-tareas?vencido=true');
                  }
                : undefined
            }
          />
          <StatBadge
            label="Mensajes sin leer"
            value={mensajesSinLeer}
            color="#8b5cf6"
            loading={loadingLeidoCounts}
            onClick={
              !loadingLeidoCounts && mensajesSinLeer > 0
                ? () => navigate('/inbox')
                : undefined
            }
          />
        </div>

        <div style={styles.grid}>
          <DashboardCard
            title="Mis Tareas"
            subtitle={
              loadingTareasResumen
                ? 'Cargando…'
                : misTareasTotal === 0
                  ? 'Sin tareas asignadas'
                  : `${misTareasTotal} tarea${misTareasTotal === 1 ? '' : 's'} · por estado`
            }
            linkLabel="Ir a Mis Tareas"
            onClick={() => navigate('/mis-tareas')}
          >
            <ResponsiveContainer width="100%" height={220}>
              {loadingTareasResumen ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                  Cargando tareas…
                </div>
              ) : misTareasPieData.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                  No hay tareas para mostrar
                </div>
              ) : (
                <PieChart>
                  <Pie
                    data={misTareasPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    dataKey="value"
                    labelLine={false}
                    label={renderPieLabel}
                    onClick={onMisTareasPieClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {misTareasPieData.map(entry => (
                      <Cell key={entry.id} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} tareas`} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              )}
            </ResponsiveContainer>
          </DashboardCard>

          <DashboardCard
            title="Bandeja de Entrada"
            subtitle={
              loadingLeidoCounts
                ? 'Cargando mensajes…'
                : `${mensajesSinLeer} mensaje${mensajesSinLeer === 1 ? '' : 's'} sin leer`
            }
            linkLabel="Ir al Inbox"
            onClick={() => navigate('/inbox')}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={inboxData} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="dia" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(139,92,246,0.08)' }}
                  formatter={(v) => `${v} mensajes`}
                />
                <Bar dataKey="mensajes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </DashboardCard>
        </div>
      </DashboardSection>

      <DashboardSection title="Proceso & Actividades">
        <div style={styles.statStrip}>
          {ACTIVIDADES_CHART_ESTADOS.map(({ id, label, color }) => {
            const entry = actividadesData.find(d => d.estado === id);
            const value = entry?.cantidad ?? 0;
            return (
              <StatBadge
                key={id}
                label={label}
                value={value}
                color={entry?.fill ?? resumenColorToHex(color)}
                loading={loadingResumen}
                onClick={
                  !loadingResumen && value > 0
                    ? () => navigateActividadesEstado(id)
                    : undefined
                }
              />
            );
          })}
          <StatBadge
            label="Actividades vencidas"
            value={actividadesVencidas}
            color="#ef4444"
            loading={loadingResumen}
            onClick={
              !loadingResumen && actividadesVencidas > 0
                ? () => {
                    playDashboardClickSound();
                    navigate('/actividades?misActividades=N&page=1&vencido=true');
                  }
                : undefined
            }
          />
        </div>

        <div style={styles.grid}>
          <DashboardCard
            title="Actividades"
            subtitle={loadingResumen ? 'Cargando…' : '(por estado)'}
            linkLabel="Ir a Actividades"
            onClick={() => navigate('/actividades?misActividades=N&page=1')}
          >
            <ResponsiveContainer width="100%" height={220}>
              {loadingResumen ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                  Cargando actividades…
                </div>
              ) : (
                <BarChart data={actividadesData} layout="vertical" barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={100} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                    formatter={(v) => `${v} tareas`}
                  />
                  <Bar dataKey="cantidad" radius={[0, 4, 4, 0]} onClick={onActividadesBarClick}>
                    {actividadesData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.fill}
                        cursor={isActividadesBarClickable(entry.estado) ? 'pointer' : 'default'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </DashboardCard>

          <DashboardCard
            title="Procesos"
            subtitle={
              loadingProcesosEstadosResumen
                ? 'Cargando…'
                : procesosTotal === 0
                  ? 'Sin procesos'
                  : `${procesosTotal} proceso${procesosTotal === 1 ? '' : 's'} · por estado`
            }
            linkLabel="Ir a Procesos"
            onClick={() => navigate('/procesos')}
          >
            <ResponsiveContainer width="100%" height={220}>
              {loadingProcesosEstadosResumen ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                  Cargando procesos…
                </div>
              ) : procesosPieData.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                  No hay procesos para mostrar
                </div>
              ) : (
                <PieChart>
                  <Pie
                    data={procesosPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    dataKey="value"
                    labelLine={false}
                    label={renderPieLabel}
                    onClick={onProcesosPieClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {procesosPieData.map(entry => (
                      <Cell key={entry.estado} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} procesos`} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              )}
            </ResponsiveContainer>
          </DashboardCard>
        </div>
      </DashboardSection>

      {canEmpresas && (
        <DashboardSection title="Empresa">
          <div style={styles.statBlock}>
            <p style={styles.statBlockSubtitle}>Vencimiento de Sellos</p>
            <div style={styles.statStrip}>
              {EMPRESA_VENCIMIENTO_INDICADORES.map(({ id, label }) => {
                const value = cantidadEmpresaVencimientoPorIndicador(sellosResumen, id);
                return (
                  <StatBadge
                    key={`sellos-${id}`}
                    label={label}
                    value={value}
                    color={EMPRESA_VENCIMIENTO_COLORS[id]}
                    loading={loadingSellosResumen}
                    onClick={
                      !loadingSellosResumen && value > 0
                        ? () => navigateEmpresasVencimiento('vencimientoSellosEstado', id)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </div>

          <div style={styles.statBlock}>
            <p style={styles.statBlockSubtitle}>Vencimiento de Fiel</p>
            <div style={styles.statStrip}>
              {EMPRESA_VENCIMIENTO_INDICADORES.map(({ id, label }) => {
                const value = cantidadEmpresaVencimientoPorIndicador(fielResumen, id);
                return (
                  <StatBadge
                    key={`fiel-${id}`}
                    label={label}
                    value={value}
                    color={EMPRESA_VENCIMIENTO_COLORS[id]}
                    loading={loadingFielResumen}
                    onClick={
                      !loadingFielResumen && value > 0
                        ? () => navigateEmpresasVencimiento('vencimientoFielEstado', id)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </div>

          <div style={styles.grid}>
            <DashboardCard
              title="Vencimiento de Sellos"
              subtitle={
                loadingSellosResumen
                  ? 'Cargando…'
                  : sellosTotal === 0
                    ? 'Sin empresas'
                    : `${sellosTotal} empresa${sellosTotal === 1 ? '' : 's'} · por estado`
              }
              linkLabel="Ir a Empresas"
              onClick={() => navigate('/empresas')}
            >
              <ResponsiveContainer width="100%" height={220}>
                {loadingSellosResumen ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                    Cargando sellos…
                  </div>
                ) : sellosPieData.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                    No hay empresas para mostrar
                  </div>
                ) : (
                  <PieChart>
                    <Pie
                      data={sellosPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      dataKey="value"
                      labelLine={false}
                      label={renderPieLabel}
                      onClick={onSellosPieClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {sellosPieData.map(entry => (
                        <Cell key={entry.indicador} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} empresas`} />
                    <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </DashboardCard>

            <DashboardCard
              title="Vencimiento de Fiel"
              subtitle={
                loadingFielResumen
                  ? 'Cargando…'
                  : fielTotal === 0
                    ? 'Sin empresas'
                    : `${fielTotal} empresa${fielTotal === 1 ? '' : 's'} · por estado`
              }
              linkLabel="Ir a Empresas"
              onClick={() => navigate('/empresas')}
            >
              <ResponsiveContainer width="100%" height={220}>
                {loadingFielResumen ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                    Cargando Fiel…
                  </div>
                ) : fielPieData.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                    No hay empresas para mostrar
                  </div>
                ) : (
                  <PieChart>
                    <Pie
                      data={fielPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      dataKey="value"
                      labelLine={false}
                      label={renderPieLabel}
                      onClick={onFielPieClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {fielPieData.map(entry => (
                        <Cell key={entry.indicador} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} empresas`} />
                    <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </DashboardCard>
          </div>
        </DashboardSection>
      )}
    </div>
  );
}

// ── Stat badge ─────────────────────────────────────────────────────────────

function StatBadge({
  label,
  value,
  color,
  loading,
  onClick,
}: {
  label: string;
  value: number;
  color: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  const clickable = !!onClick && !loading;

  const handleClick = () => {
    playDashboardClickSound();
    onClick?.();
  };

  return (
    <div
      style={{
        ...styles.statBadge,
        borderLeft: `4px solid ${color}`,
        cursor: clickable ? 'pointer' : undefined,
      }}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={
        clickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <span style={{ ...styles.statValue, color }}>{loading ? '…' : value}</span>
      <span style={styles.statLabel}>{label}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  pageHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'var(--crm-surface)',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#6366f1',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  },
  pageTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--crm-text-primary)',
  },
  pageSubtitle: {
    margin: 0,
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--crm-text-primary)',
    paddingBottom: 8,
    borderBottom: '2px solid var(--crm-border)',
  },
  statBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  statBlockSubtitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
  },
  statStrip: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  statBadge: {
    background: 'var(--crm-surface)',
    borderRadius: 10,
    padding: '14px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    flex: '1 1 160px',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 12,
    color: 'var(--crm-text-secondary)',
    fontWeight: 500,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 20,
  },
  card: {
    background: 'var(--crm-surface)',
    borderRadius: 12,
    padding: '20px 20px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--crm-text-primary)',
  },
  cardSubtitle: {
    margin: 0,
    fontSize: 12,
    color: 'var(--crm-text-secondary)',
  },
  chartArea: {
    marginTop: 4,
  },
  linkBtn: {
    background: 'none',
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: '#3b82f6',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'background 0.15s',
  },
};
